import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PieceDetail } from '../api'
import { describeError } from '../../lib/describeError'
import { CropPreview, SlotPreviews } from './FramingDialog'
import { applyPhotoHelp, askPhotoHelp, discardPhotoHelp, listPhotoHelp } from './photoHelpApi'
import { canDismiss, captionLine, changeTitle, isWorking, outcomeText, pendingChanges, type PhotoChange, type PhotoRequest } from './photoHelp'
import { SAVE_FIRST } from './pieceHelpers'
import { btn, btnPrimary, btnSmall, errorText, field } from './ui'

const POLL_MS = 3000

type Props = {
  detail: PieceDetail
  /** Save the edits waiting first; false refuses the write. */
  beforeWrite: () => Promise<boolean>
  onDetail: (d: PieceDetail) => void
  /** Refetch the piece: the apply reply lacks updated_at, the checklist and the summary. */
  onChanged: () => void
  /** The body text changed on the server: bring the editor in step. */
  onBodyReplaced: (html: string) => void
}

/** Ask the desk about the piece's photos; review what it proposes and apply or discard it. */
export function PhotoHelpBox({ detail, beforeWrite, onDetail, onChanged, onBodyReplaced }: Props) {
  const id = detail.id
  const qc = useQueryClient()
  const key = ['photo-help', id]
  const list = useQuery({
    queryKey: key,
    queryFn: () => listPhotoHelp(id),
    refetchInterval: (q) => (q.state.data?.requests.some(isWorking) ? POLL_MS : false),
  })
  const [text, setText] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: key })

  const ask = useMutation({
    mutationFn: () => askPhotoHelp(id, text.trim()),
    onSuccess: () => { setText(''); void refresh() },
  })
  const apply = useMutation({
    mutationFn: async (v: { reqId: number; changeIds: string[] | null }) => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      return applyPhotoHelp(id, v.reqId, v.changeIds)
    },
    onSuccess: (r) => {
      const bodyChanged = (r.detail.body_html ?? '') !== (detail.body_html ?? '')
      onDetail(r.detail as PieceDetail)
      onChanged()
      if (bodyChanged) onBodyReplaced(r.detail.body_html ?? '')
      void refresh()
    },
  })
  const discard = useMutation({
    mutationFn: (v: { reqId: number; changeIds: string[] | null }) => discardPhotoHelp(id, v.reqId, v.changeIds),
    onSuccess: () => { void refresh() },
  })
  const busy = apply.isPending || discard.isPending
  const requests = list.data?.requests ?? []
  const error = [ask, apply, discard].find((m) => m.error)?.error

  return (
    <section aria-labelledby="photo-help-h" className="mt-4 flex flex-col gap-3 rounded-lg border border-ink-500 bg-ink-800 p-4 font-ui text-paper">
      <h2 id="photo-help-h" className="m-0 font-serif text-[18px] font-normal">Ask about photos</h2>
      <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); if (text.trim()) ask.mutate() }}>
        <textarea className={field} rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)}
                  aria-label="Ask about photos" placeholder="e.g. Use a photo of the P-38J in flight as the lead" />
        <button type="submit" className={`${btnPrimary} self-start`} disabled={!text.trim() || ask.isPending}>
          {ask.isPending ? 'Sending…' : 'Send'}
        </button>
      </form>
      {error && <p role="alert" className={errorText}>{describeError(error)}</p>}
      {list.isError && <p role="alert" className={errorText}>Could not load earlier requests. {describeError(list.error)}</p>}
      {requests.map((r) => (
        <RequestCard key={r.id} r={r} busy={busy}
                     onApply={(changeIds) => apply.mutate({ reqId: r.id, changeIds })}
                     onDiscard={(changeIds) => discard.mutate({ reqId: r.id, changeIds })} />
      ))}
    </section>
  )
}

function RequestCard({ r, busy, onApply, onDiscard }: {
  r: PhotoRequest
  busy: boolean
  onApply: (changeIds: string[] | null) => void
  onDiscard: (changeIds: string[] | null) => void
}) {
  const pending = r.status === 'ready' ? pendingChanges(r) : []
  return (
    <article className="flex flex-col gap-2 border-t border-ink-500 pt-3">
      <p className="m-0 text-[14px] font-semibold">{r.instruction}</p>
      {isWorking(r) && <p role="status" className="m-0 text-[13px] text-paper-dim">Working…</p>}
      {r.status === 'failed' && <p className={errorText}>{r.error || 'The request failed.'}</p>}
      {r.reply && <p className="m-0 whitespace-pre-line text-[14px] text-paper-dim">{r.reply}</p>}
      {r.changes.map((c) => <ChangeCard key={c.id} c={c} busy={busy} ready={r.status === 'ready'}
                                        onApply={() => onApply([c.id])} onDiscard={() => onDiscard([c.id])} />)}
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} disabled={busy} onClick={() => onApply(null)}>Apply all</button>
          <button type="button" className={btn} disabled={busy} onClick={() => onDiscard(null)}>Discard</button>
        </div>
      )}
      {canDismiss(r) && (
        <button type="button" className={`${btn} self-start`} disabled={busy} onClick={() => onDiscard(null)}>Dismiss</button>
      )}
    </article>
  )
}

function ChangeCard({ c, busy, ready, onApply, onDiscard }: {
  c: PhotoChange
  busy: boolean
  ready: boolean
  onApply: () => void
  onDiscard: () => void
}) {
  const open = ready && c.outcome == null
  return (
    <div className="flex flex-col gap-2 rounded-md border border-ink-500 bg-ink-700 p-3">
      <p className="m-0 text-[13px] font-semibold">{changeTitle(c)}</p>
      {c.text && <p className="m-0 text-[13px] text-paper-dim">{c.text}</p>}
      {captionLine(c) && <p className="m-0 text-[13px] italic text-paper-dim">{captionLine(c)}</p>}
      <ChangePicture c={c} />
      {c.outcome != null && <p className={`m-0 text-[12px] ${c.outcome === 'refused' ? 'text-radar-red' : 'text-paper-dim'}`}>{outcomeText(c)}</p>}
      {open && (
        <div className="flex gap-2">
          <button type="button" className={btnSmall} disabled={busy} onClick={onApply}>Apply</button>
          <button type="button" className={btnSmall} disabled={busy} onClick={onDiscard}>Discard</button>
        </div>
      )}
    </div>
  )
}

/** Before and after thumbnails; a reframe or trim is drawn on the photo now in that place. */
function ChangePicture({ c }: { c: PhotoChange }) {
  const side = c.before?.src ? c.before : c.after
  const now = side?.src ?? null
  const [w, h] = [side?.width ?? null, side?.height ?? null]
  if (c.kind === 'framing' && c.target === 'featured' && now) {
    return <SlotPreviews src={now} width={w} height={h} focal={c.focal} box={c.subject_box} fit={c.fit} />
  }
  if (c.kind === 'trim' && now && c.box) {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Thumb label="Before" src={now} />
        <div className="flex flex-col gap-1">
          <span className="text-[12px] text-paper-dim">After</span>
          <CropPreview src={now} box={c.box} width={w} height={h} />
        </div>
      </div>
    )
  }
  if (!c.before?.src && !c.after?.src) return null
  return (
    <div className="grid grid-cols-2 gap-2">
      {c.before?.src && <Thumb label="Before" src={c.before.src} />}
      {c.after?.src && <Thumb label="After" src={c.after.src} />}
    </div>
  )
}

function Thumb({ label, src }: { label: string; src: string }) {
  return (
    <figure className="m-0 flex flex-col gap-1">
      <figcaption className="text-[12px] text-paper-dim">{label}</figcaption>
      <img src={src} alt="" className="aspect-[3/2] w-full rounded object-cover" />
    </figure>
  )
}
